import {css, StyleSheet} from 'aphrodite';
import React, {useCallback} from 'react';

import BaseStatusButton, {BaseStatusButtonProps} from 'notebook/worksheets/components/BaseStatusButton';
import AmendStepModal from 'notebook/worksheets/modals/AmendStepModal';
import WorksheetStepStatusIcon from 'notebook/worksheets/steps/WorksheetStepStatusIcon';
import {
  NO_USER,
  WORKSHEET_STEP_TERMINAL_STATUSES,
  WorksheetTaskStatusType,
} from 'notebook/worksheets/WorksheetConstants';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Avatar, {HasAvatar} from 'ui-library/avatar/Avatar';
import {AvatarSize} from 'ui-library/avatar/AvatarConstants';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE_LIGHT} from 'ui-library/Colors';
import legacyRunModal from 'ui-library/legacyRunModal';
import Option from 'ui-library/menus/Option';

const TASK_STATUS_TO_BUTTON_PROPS: {[status: string]: BaseStatusButtonProps} = {
  [WorksheetTaskStatusType.READY]: {
    text: 'Complete step',
    buttonStyle: ButtonStyle.PRIMARY,
    isDropdown: false,
  },
  [WorksheetTaskStatusType.AMENDING]: {
    text: 'Amending...',
    buttonStyle: ButtonStyle.PRIMARY,
    isDropdown: false,
    icon: <WorksheetStepStatusIcon status={WorksheetTaskStatusType.AMENDING} />,
  },
  [WorksheetTaskStatusType.NOT_READY]: {
    text: 'Not ready',
    buttonStyle: ButtonStyle.TERTIARY_GRAY,
    isDropdown: false,
  },
  [WorksheetTaskStatusType.COMPLETED]: {
    text: 'Completed',
    buttonStyle: ButtonStyle.TERTIARY_WHITE,
    isDropdown: true,
    icon: <WorksheetStepStatusIcon status={WorksheetTaskStatusType.COMPLETED} />,
  },
  [WorksheetTaskStatusType.COMPLETING]: {
    text: 'Completing...',
    buttonStyle: ButtonStyle.TERTIARY_WHITE,
    isDropdown: true,
    icon: <WorksheetStepStatusIcon status={WorksheetTaskStatusType.COMPLETING} />,
  },
  [WorksheetTaskStatusType.SKIPPED]: {
    text: 'Skipped',
    buttonStyle: ButtonStyle.TERTIARY_WHITE,
    isDropdown: true,
    icon: <WorksheetStepStatusIcon status={WorksheetTaskStatusType.SKIPPED} />,
  },
  [WorksheetTaskStatusType.SKIPPING]: {
    text: 'Skipping...',
    buttonStyle: ButtonStyle.TERTIARY_WHITE,
    isDropdown: true,
    icon: <WorksheetStepStatusIcon status={WorksheetTaskStatusType.SKIPPING} />,
  },
  [WorksheetTaskStatusType.FAILED]: {
    text: 'Failed',
    buttonStyle: ButtonStyle.TERTIARY_WHITE,
    isDropdown: true,
    icon: <WorksheetStepStatusIcon status={WorksheetTaskStatusType.FAILED} />,
  },
  [WorksheetTaskStatusType.FAILING]: {
    text: 'Failing...',
    buttonStyle: ButtonStyle.TERTIARY_WHITE,
    isDropdown: true,
    icon: <WorksheetStepStatusIcon status={WorksheetTaskStatusType.FAILING} />,
  },
  // Note: We will not have the following statuses for Step tasks, but this keeps TypeScript happy since all
  // possible keys for WorksheetTaskStatusType are fulfilled
  [WorksheetTaskStatusType.PENDING]: {
    text: 'Pending',
    buttonStyle: ButtonStyle.TERTIARY_WHITE,
    isDropdown: true,
    icon: <WorksheetStepStatusIcon status={WorksheetTaskStatusType.PENDING} />,
  },
  [WorksheetTaskStatusType.IN_PROGRESS]: {
    text: 'In Progress',
    buttonStyle: ButtonStyle.TERTIARY_WHITE,
    isDropdown: true,
    icon: <WorksheetStepStatusIcon status={WorksheetTaskStatusType.IN_PROGRESS} />,
  },
};

// This component only utilizes a subset of the WorksheetTaskStatusType enum
export type StepTransitionStatusType = Extract<
  WorksheetTaskStatusType,
  | WorksheetTaskStatusType.COMPLETED
  | WorksheetTaskStatusType.FAILED
  | WorksheetTaskStatusType.SKIPPED
  | WorksheetTaskStatusType.READY
>;

interface WorksheetStepButtonProps {
  // Handler that accepts a submission option to post
  onClick: (submitOption: StepTransitionStatusType) => Promise<void>;
  status: WorksheetTaskStatusType;
  lastEditedUser?: HasAvatar;
  timestamp?: string;
  locked: boolean;
  className?: string;
}

/**
 * Button to submit a step with status 'COMPLETED'.
 *
 * This component is part of the larger {@link WorksheetStepStatusBadge}
 * parent component.
 */
export default function WorksheetStepButton({
  onClick,
  status,
  lastEditedUser,
  timestamp,
  locked,
  className,
}: WorksheetStepButtonProps): JSX.Element {
  const completeCallback: () => void = useCallback(async () => {
    await onClick(WorksheetTaskStatusType.COMPLETED);
  }, [onClick]);

  const readyCallback = useCallback(() => {
    onClick(WorksheetTaskStatusType.READY);
  }, [onClick]);

  return (
    <BaseStatusButton
      className={className}
      menuOptions={getAmendStepDropdownMenuOptions(
        status,
        lastEditedUser ?? NO_USER,
        readyCallback,
        timestamp
      )}
      onClick={status === WorksheetTaskStatusType.READY ? completeCallback : undefined}
      disabled={locked}
      {...TASK_STATUS_TO_BUTTON_PROPS[status]}
    />
  );
}

function getAmendStepDropdownMenuOptions(
  status: WorksheetTaskStatusType,
  lastEditedUser: HasAvatar,
  onSubmit: () => void,
  timestamp?: string
): Array<JSX.Element> | undefined {
  // do not provide the ability to amend steps that are not in terminal states
  if (!WORKSHEET_STEP_TERMINAL_STATUSES.includes(status)) {
    return undefined;
  }

  return [
    <Option contentClassName={css(styles.dropdownOptionContent)} isHovered={false} key="step-info">
      <div className={css(styles.dropdownDetails)}>
        <div className={css(styles.avatar)}>
          <Avatar entity={lastEditedUser} size={AvatarSize.SMALL} />
        </div>
        <div className={css(styles.textContainer)}>
          <span className={css(styles.statusLabel)}>{`Step ${TASK_STATUS_TO_BUTTON_PROPS[
            status
          ].text.toLowerCase()}`}</span>
          <br />
          {timestamp ?? <span>{timestamp}</span>}
        </div>
      </div>
    </Option>,
    <Option
      contentClassName={css(styles.dropdownOptionContent)}
      onSelect={() => {
        legacyRunModal(AmendStepModal, {onRequestSubmit: onSubmit});
      }}
      key="label"
    >
      <span
        className={css(styles.amendLinkLabel)}
        {...getTestProps({component: 'WorksheetStepButton', element: 'amendStepOption'})}
      >
        Amend step
      </span>
    </Option>,
  ];
}

const styles = StyleSheet.create({
  dropdownDetails: {
    display: 'flex',
    cursor: 'default',
  },
  dropdownOptionContent: {
    padding: '0px',
  },
  avatar: {
    alignSelf: 'center',
  },
  textContainer: {
    marginLeft: 8,
  },
  statusLabel: {
    fontSize: '13px',
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    paddingBottom: 4,
  },
  amendLinkLabel: {
    color: BLUE_LIGHT,
    textAlign: 'left',
    marginLeft: '36px',
  },
});
